export function validateWireTailings(panelConfig) {
  const missingRequirements = [];

  const connectors = Array.isArray(panelConfig?.connectors) ? panelConfig.connectors : [];
  const wireTailings = Array.isArray(panelConfig?.wireTailings) ? panelConfig.wireTailings : [];

  if (connectors.length === 0) {
    return {
      isValid: false,
      missingRequirements: ['Please add at least one connector to the panel']
    };
  }

  // Panel Builder requires an explicit tail selection per connector:
  // either "No wire tail" OR a fully configured wire tailing.
  connectors.forEach((connector, index) => {
    const tailing = wireTailings.find((t) => t?.connectorId === connector.id);
    const label = `Connector ${index + 1}`;

    if (!tailing) {
      missingRequirements.push(`${label}: Please choose “Configure wire tailing” or “No wire tail”`);
      return;
    }

    if (tailing.noTail) return;

    if (!tailing.connectors?.endB?.connector) {
      missingRequirements.push(`${label}: Please select a tail connector`);
    }

    if (!tailing.wireType) {
      missingRequirements.push(`${label}: Please select a wire type`);
    }

    if (!tailing.length || tailing.length <= 0) {
      missingRequirements.push(`${label}: Please set a valid wire length`);
    }
  });

  return {
    isValid: missingRequirements.length === 0,
    missingRequirements
  };
}

export function findNextAvailablePosition({
  preferredCol,
  preferredRow,
  gridCols,
  gridRows,
  connectors,
  connectorWidth = 9,
  connectorHeight = 10,
  getConnectorSpan,
  maxSearchRadius = 10
}) {
  const placed = Array.isArray(connectors) ? connectors : [];
  const getSpanForPlaced = typeof getConnectorSpan === 'function'
    ? getConnectorSpan
    : () => ({ width: connectorWidth, height: connectorHeight });

  const isPositionAvailable = (col, row) => {
    if (
      col < 0 ||
      row < 0 ||
      col + connectorWidth > gridCols ||
      row + connectorHeight > gridRows
    ) {
      return false;
    }

    return !placed.some(conn => {
      const connRow = conn.position?.row ?? 0;
      const connCol = conn.position?.col ?? 0;
      const span = getSpanForPlaced(conn) || {};
      const connW = span.width ?? connectorWidth;
      const connH = span.height ?? connectorHeight;

      // Same overlap logic as PanelBuilder: allow adjacent placement.
      return !(
        col + connectorWidth < connCol ||
        col > connCol + connW ||
        row + connectorHeight < connRow ||
        row > connRow + connH
      );
    });
  };

  if (isPositionAvailable(preferredCol, preferredRow)) {
    return { col: preferredCol, row: preferredRow };
  }

  const maxRadius = Math.min(maxSearchRadius, Math.max(gridCols, gridRows));
  for (let radius = 1; radius <= maxRadius; radius++) {
    const positions = [];

    for (let offset = 0; offset < radius; offset++) {
      positions.push({ col: preferredCol + radius, row: preferredRow + offset });
      positions.push({ col: preferredCol + radius, row: preferredRow - offset });
      positions.push({ col: preferredCol + offset, row: preferredRow + radius });
      positions.push({ col: preferredCol - offset, row: preferredRow + radius });
      positions.push({ col: preferredCol - radius, row: preferredRow + offset });
      positions.push({ col: preferredCol - radius, row: preferredRow - offset });
      positions.push({ col: preferredCol + offset, row: preferredRow - radius });
      positions.push({ col: preferredCol - offset, row: preferredRow - radius });
    }

    const uniquePositions = positions.filter(
      (pos, index, self) =>
        index === self.findIndex(p => p.col === pos.col && p.row === pos.row)
    );

    for (const pos of uniquePositions) {
      if (isPositionAvailable(pos.col, pos.row)) return pos;
    }
  }

  return { col: preferredCol, row: preferredRow };
}

